import axios from 'axios'
import { getToken } from '../utils/Cookies'
import { camelCase, isArray, isObject } from 'lodash'
import { ApiErrorKind, apiErrorKindOf } from './apiErrors'

const api = axios.create({
  baseURL: `${process.env.REACT_APP_API_BASE_URL}`,
})

export const ResponseType = {
  JSON: 'application/json',
  PDF: 'application/pdf',
  XLSX: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  XLS: 'application/vnd.ms-excel',
  CSV: 'text/csv',
  HTML: 'text/html',
  XML: 'text/xml',
  TEXT: 'text/plain',
  BLOB: 'application/octet-stream',
}

const fakeApi = axios.create({
  baseURL: `${process.env.REACT_APP_FAKE_API_BASE_URL}fake/api/`,
})

api.interceptors.request.use(config => {
  config.headers.Authorization = `Bearer ${getToken()}`
  return config
})

let onUnauthenticated = null

/**
 * Registers what to do when the server rejects our token.
 *
 * This module cannot reach the auth service on its own: constructing an AuthService here would
 * stand up a second oidc-client UserManager racing the app's one over the same localStorage.
 * The auth provider owns the instance and hands the behaviour down instead.
 */
export const setUnauthenticatedHandler = handler => {
  onUnauthenticated = handler
}

// One recovery attempt per tab. A 401 means the server rejected a token oidc-client still
// considers valid, so its own expiry timer will never fire and nothing else recovers the session
// — but if the freshly issued token is rejected too, re-authenticating again just bounces the
// user through the identity server forever. Any successful response clears the latch, because at
// that point a later 401 is a new problem rather than the same one repeating.
const REAUTH_ATTEMPTED_KEY = 'dashboard.reauthAttempted'

api.interceptors.response.use(
  response => {
    window.sessionStorage.removeItem(REAUTH_ATTEMPTED_KEY)
    return response
  },
  error => {
    const shouldReauthenticate =
      apiErrorKindOf(error) === ApiErrorKind.Unauthenticated &&
      onUnauthenticated !== null &&
      window.sessionStorage.getItem(REAUTH_ATTEMPTED_KEY) === null

    if (shouldReauthenticate) {
      window.sessionStorage.setItem(REAUTH_ATTEMPTED_KEY, 'true')
      onUnauthenticated()
    }

    return Promise.reject(error)
  }
)

/**
 * Endpoints
 * @param uri
 * @returns {Promise<any>}
 */
const Endpoints = {
  Options: 'options',
  Reports: 'reports',
  LiveReport: 'LiveReport',
  Comments: 'comments',
  Search: 'search',
  Structure: 'structure',
  Filters: 'filters',
  HighchartOptions: 'highchart-options',
  OptionsTableFilters: 'options-table-filters',
  Plan: 'Plan',
  Report: 'Report',
  Chart: 'chart',
  Wizard: 'Wizard',
  User: 'user',
  Permission: 'Permission',
}

/**
 * @param uri
 * @param options.throwOnError Reject instead of resolving to an empty body. Off by default:
 *   every caller predating the response interceptor was written against the swallowing
 *   behaviour and would start throwing where it used to render nothing. Opt in from a caller
 *   whose screen can actually show the failure.
 * @returns {Promise<any>}
 */
const fetch = async (uri, { throwOnError = false } = {}) => {
  try {
    const response = await api.get(uri, {
      responseType: 'json',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    })
    return response
  } catch (error) {
    if (throwOnError) throw error
    return { data: {}, message: error.message }
  }
}
const fetchUint8 = async uri => {
  const response = await api.get(uri, { responseType: 'arraybuffer' })
  return new Uint8Array(response.data)
}
const post = async (uri, data, config = {}) => {
  const response = await api.post(uri, data, config)
  return response
}
const put = async (uri, data, config = {}) => {
  const response = await api.put(uri, data, config)
  return response
}
const patch = async (uri, data) => {
  const response = await api.patch(uri, data)
  return response
}
const remove = async (uri, id) => {
  const response = await api.delete(uri, id)
  return response
}

/**
 * Converts all keys of an object to lowercase
 * @param json
 * @returns {Dictionary<{}>|*}
 */

function normalizeKeys(json) {
  if (isArray(json)) {
    return json.map(item => normalizeKeys(item))
  } else if (isObject(json)) {
    const newObject = {}
    for (const key in json) {
      newObject[camelCase(key)] = normalizeKeys(json[key])
    }
    return newObject
  } else {
    return json
  }
}

export {
  api,
  fakeApi,
  fetch,
  fetchUint8,
  post,
  put,
  patch,
  remove,
  Endpoints,
  normalizeKeys,
}
